import { useState, useEffect, useCallback } from 'react';
import type { RehearsalQuestion, QuestionType, ScoreLabel } from '@/types';

interface Props {
  question: RehearsalQuestion;
  onSubmit: (answer: string, answerMode: 'voice' | 'typed') => Promise<void>;
  onSkip: (reason: 'manual' | 'timeout') => Promise<void>;
  onOpenTypedAnswer?: () => void;
  isEvaluating: boolean;
  timerDurationMs?: number | null; // null for Easy mode, 15000 for Medium, 10000 for Hard
  isListeningForVoice?: boolean; // true when voice answer is being captured
  voiceTranscript?: string; // current voice transcript being captured
}

const TYPE_LABELS: Record<QuestionType, { label: string; color: string }> = {
  talking_point: { label: 'Recall', color: 'bg-green-100 text-green-700' },
  technical: { label: 'Technical', color: 'bg-blue-100 text-blue-700' },
  contrarian: { label: 'Objection', color: 'bg-orange-100 text-orange-700' },
  clarification: { label: 'Clarify', color: 'bg-purple-100 text-purple-700' },
  edge_case: { label: 'Edge Case', color: 'bg-red-100 text-red-700' },
};

const SCORE_LABELS: Record<ScoreLabel, { color: string; icon: string }> = {
  'Excellent': { color: 'text-green-600', icon: '🌟' },
  'Good': { color: 'text-blue-600', icon: '✓' },
  'Acceptable': { color: 'text-yellow-600', icon: '○' },
  'Needs Work': { color: 'text-orange-600', icon: '⚠' },
  'Missed': { color: 'text-red-600', icon: '✗' },
};

export function QuestionCard({
  question,
  onSubmit,
  onSkip,
  onOpenTypedAnswer,
  isEvaluating,
  timerDurationMs,
  isListeningForVoice,
  voiceTranscript,
}: Props) {
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemainingMs, setTimeRemainingMs] = useState<number | null>(timerDurationMs ?? null);
  const [showVoiceTip, setShowVoiceTip] = useState(false);

  const hasResponse = question.respondedAt !== undefined;
  const typeInfo = TYPE_LABELS[question.questionType] || TYPE_LABELS.talking_point;
  const hasTimer = timerDurationMs != null && timerDurationMs > 0;

  // Timer countdown effect
  useEffect(() => {
    if (!hasTimer || hasResponse || isEvaluating || isSubmitting) {
      return;
    }

    // Reset timer when question changes
    setTimeRemainingMs(timerDurationMs);

    const interval = setInterval(() => {
      setTimeRemainingMs(prev => {
        if (prev === null || prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [hasTimer, timerDurationMs, hasResponse, isEvaluating, isSubmitting, question.id]);

  // Auto-skip on timeout
  useEffect(() => {
    if (timeRemainingMs !== null && timeRemainingMs <= 0 && !hasResponse && !isSubmitting && !isEvaluating) {
      handleSkip('timeout');
    }
  }, [timeRemainingMs, hasResponse, isSubmitting, isEvaluating]);

  const handleSubmit = useCallback(async (mode: 'voice' | 'typed' = 'typed') => {
    const answerText = mode === 'voice' && voiceTranscript ? voiceTranscript : answer.trim();
    if (!answerText || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(answerText, mode);
    } finally {
      setIsSubmitting(false);
    }
  }, [answer, voiceTranscript, isSubmitting, onSubmit]);

  const handleSkip = useCallback(async (reason: 'manual' | 'timeout' = 'manual') => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSkip(reason);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, onSkip]);

  // Calculate timer percentage for progress ring
  const timerPercent = hasTimer && timerDurationMs
    ? Math.max(0, (timeRemainingMs ?? 0) / timerDurationMs) * 100
    : 100;
  const timerSeconds = timeRemainingMs !== null ? Math.ceil(timeRemainingMs / 1000) : 0;
  const isTimerLow = timerSeconds <= 5;
  const isTimerCritical = timerSeconds <= 3;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSubmit('typed');
    }
  };

  // Show result view after response
  if (hasResponse && !question.skipped) {
    const scoreLabel = question.label || 'Good';
    const scoreInfo = SCORE_LABELS[scoreLabel] || SCORE_LABELS['Good'];

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 space-y-3">
        {/* Score Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-2xl ${scoreInfo.color}`}>{scoreInfo.icon}</span>
            <div>
              <div className={`text-lg font-bold ${scoreInfo.color}`}>
                {question.score !== undefined ? Math.round(question.score) : '--'}/100
              </div>
              <div className={`text-xs font-medium ${scoreInfo.color}`}>{scoreLabel}</div>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
        </div>

        {/* Question */}
        <div className="text-sm text-gray-600 italic border-l-2 border-gray-200 pl-3">
          "{question.questionText}"
        </div>

        {/* Your Answer */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">Your answer:</div>
          <div className="text-sm text-gray-700">{question.responseText}</div>
        </div>

        {/* Score Breakdown */}
        {question.scoringBreakdown && (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-xs text-gray-500">Accuracy</div>
              <div className="font-bold text-gray-700">{question.scoringBreakdown.accuracy}%</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-xs text-gray-500">Complete</div>
              <div className="font-bold text-gray-700">{question.scoringBreakdown.completeness}%</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-xs text-gray-500">Clarity</div>
              <div className="font-bold text-gray-700">{question.scoringBreakdown.clarity}%</div>
            </div>
          </div>
        )}

        {/* Feedback */}
        {question.feedback && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-xs text-blue-600 font-medium mb-1">Feedback</div>
            <div className="text-sm text-blue-700">{question.feedback}</div>
          </div>
        )}
      </div>
    );
  }

  // Show skipped view
  if (hasResponse && question.skipped) {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2 opacity-75">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">Question skipped</span>
          <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
        </div>
        <div className="text-sm text-gray-500 italic">"{question.questionText}"</div>
      </div>
    );
  }

  // Show question input view
  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 overflow-hidden ${
      isTimerCritical ? 'border-red-300 animate-pulse' : isTimerLow ? 'border-orange-300' : 'border-blue-200'
    }`}>
      {/* Header with Timer */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        isTimerCritical ? 'bg-red-50' : isTimerLow ? 'bg-orange-50' : 'bg-blue-50'
      }`}>
        <div className="flex items-center gap-2">
          <span className={`text-lg ${isTimerCritical ? 'text-red-500' : isTimerLow ? 'text-orange-500' : 'text-blue-500'}`}>
            {isListeningForVoice ? '🎤' : '❓'}
          </span>
          <span className={`font-medium text-sm ${
            isTimerCritical ? 'text-red-700' : isTimerLow ? 'text-orange-700' : 'text-blue-700'
          }`}>
            {isListeningForVoice ? 'Listening...' : 'Practice Question'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Timer Display */}
          {hasTimer && (
            <div className="relative flex items-center justify-center">
              <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                <circle
                  className="text-gray-200"
                  strokeWidth="3"
                  stroke="currentColor"
                  fill="transparent"
                  r="15"
                  cx="18"
                  cy="18"
                />
                <circle
                  className={`transition-all duration-100 ${
                    isTimerCritical ? 'text-red-500' : isTimerLow ? 'text-orange-500' : 'text-blue-500'
                  }`}
                  strokeWidth="3"
                  strokeDasharray={94.2}
                  strokeDashoffset={94.2 * (1 - timerPercent / 100)}
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="15"
                  cx="18"
                  cy="18"
                />
              </svg>
              <span className={`absolute text-xs font-bold ${
                isTimerCritical ? 'text-red-600' : isTimerLow ? 'text-orange-600' : 'text-blue-600'
              }`}>
                {timerSeconds}
              </span>
            </div>
          )}
          <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
        </div>
      </div>

      {/* Question */}
      <div className="p-4 space-y-4">
        <p className="text-gray-800 font-medium">{question.questionText}</p>

        {/* Voice Instruction for Medium/Hard mode */}
        {hasTimer && !isListeningForVoice && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVoiceTip(!showVoiceTip)}
              className="text-xs text-blue-500 hover:text-blue-600 underline cursor-pointer"
            >
              How to answer with voice
            </button>
            {showVoiceTip && (
              <div className="absolute left-0 top-6 bg-blue-900 text-white text-xs rounded-lg p-3 shadow-lg z-10 w-64">
                <div className="font-medium mb-1">Voice Answer</div>
                <p>Say <strong>"To answer your question..."</strong> followed by your response.</p>
                <p className="mt-1 text-blue-200">Your answer will be captured automatically.</p>
                <button
                  onClick={() => setShowVoiceTip(false)}
                  className="absolute top-1 right-2 text-blue-200 hover:text-white"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        )}

        {/* Voice Listening Indicator */}
        {isListeningForVoice && voiceTranscript && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-green-600 font-medium">Capturing your answer...</span>
            </div>
            <p className="text-sm text-green-700 italic">"{voiceTranscript}"</p>
          </div>
        )}

        {/* Answer Input - hidden when listening for voice */}
        {!isListeningForVoice && (
          <div className="space-y-2">
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={hasTimer ? "Or type your answer here..." : "Type your answer..."}
              disabled={isEvaluating || isSubmitting}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            />
            <p className="text-xs text-gray-400">Press ⌘+Enter to submit</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => handleSkip('manual')}
            disabled={isEvaluating || isSubmitting || isListeningForVoice}
            className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Skip
          </button>
          {isListeningForVoice ? (
            <button
              onClick={() => handleSubmit('voice')}
              disabled={!voiceTranscript || isEvaluating || isSubmitting}
              className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(isEvaluating || isSubmitting) ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Evaluating...
                </>
              ) : (
                'Submit Voice Answer'
              )}
            </button>
          ) : (
            <button
              onClick={() => handleSubmit('typed')}
              disabled={!answer.trim() || isEvaluating || isSubmitting}
              className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(isEvaluating || isSubmitting) ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Evaluating...
                </>
              ) : (
                'Submit Answer'
              )}
            </button>
          )}
        </div>

        {/* Type Answer Button for voice mode */}
        {hasTimer && !isListeningForVoice && onOpenTypedAnswer && (
          <button
            onClick={onOpenTypedAnswer}
            className="w-full py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Prefer to type a longer answer? Click here
          </button>
        )}
      </div>
    </div>
  );
}
